import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Bell, MapPin, Trophy } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../api";
import { useLang } from "../i18n";
import { Reveal, SectionHead } from "../components/Section";
import { IMAGES } from "../data/site";

export default function Vote() {
    const { t } = useLang();
    const [cities, setCities] = useState([]);
    const [active, setActive] = useState(true);
    const [voted, setVoted] = useState(() => localStorage.getItem("lakou_voted") || "");
    const [notifyCity, setNotifyCity] = useState(null);
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState("");

    const load = () => api.get("/votes").then((r) => {
        setCities(r.data.cities || []);
        setActive(r.data.active !== false);
    }).catch(() => {});

    useEffect(() => { load(); }, []);

    const vote = async (city) => {
        if (voted || !active) return;
        setBusy(city.id);
        try {
            await api.post("/votes", { city_id: city.id });
            localStorage.setItem("lakou_voted", city.id);
            setVoted(city.id);
            setNotifyCity(city);
            toast.success(`${city.name} — your vote counts.`);
            load();
        } catch (e) {
            toast.error(apiError(e, "Could not register your vote."));
            if (e.response?.status === 409) { localStorage.setItem("lakou_voted", "locked"); setVoted("locked"); }
        } finally {
            setBusy("");
        }
    };

    const subscribeCity = async (e) => {
        e.preventDefault();
        try {
            await api.post("/votes/interest", { city_id: notifyCity.id, email });
            toast.success("We'll email you the moment your city is confirmed.");
            setNotifyCity(null);
        } catch (err) {
            toast.error(apiError(err));
        }
    };

    return (
        <div data-testid="vote-page" className="pt-32 pb-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
                    <div className="lg:col-span-2">
                        <SectionHead eyebrow="The road ahead" title={t("vote_title")} testId="vote-title" />
                        <Reveal delay={0.1}>
                            <p className="mt-6 text-lakou-grey leading-relaxed">{t("vote_sub")}</p>
                            <div className="mt-6 rounded-3xl overflow-hidden border border-lakou-gold/20 aspect-[4/3]">
                                <img src={IMAGES.palm} alt="Haiti coastline" className="w-full h-full object-cover" />
                            </div>
                            {!active && <p className="mt-6 text-sm text-lakou-sand" data-testid="vote-closed-note">Voting is currently closed.</p>}
                        </Reveal>
                    </div>
                    <div className="lg:col-span-3 space-y-3">
                        {cities.map((c, i) => {
                            const isVoted = voted === c.id;
                            return (
                                <Reveal key={c.id} delay={i * 0.04}>
                                    <div data-testid={`vote-city-${c.id}`}
                                        className={`rounded-2xl border p-5 transition-colors duration-300 ${isVoted ? "border-lakou-gold bg-lakou-gold/10" : "border-lakou-gold/15 bg-lakou-coal/60"}`}>
                                        <div className="flex items-center justify-between gap-4">
                                            <div className="flex items-center gap-4 min-w-0">
                                                <span className={`w-9 h-9 shrink-0 rounded-full flex items-center justify-center font-mono2 text-xs ${c.rank === 1 ? "bg-lakou-gold text-lakou-ink" : "border border-lakou-gold/30 text-lakou-gold"}`}>
                                                    {c.rank === 1 ? <Trophy size={14} /> : c.rank}
                                                </span>
                                                <div className="min-w-0">
                                                    <div className="font-display font-bold text-lakou-cream truncate">{c.name}</div>
                                                </div>
                                            </div>
                                            <motion.button
                                                whileTap={{ scale: 0.95 }}
                                                onClick={() => vote(c)}
                                                disabled={!!voted || !active || busy === c.id}
                                                data-testid={`vote-button-${c.id}`}
                                                className={`shrink-0 inline-flex items-center gap-2 rounded-full font-display font-bold text-[11px] uppercase tracking-[0.12em] px-5 py-2.5 transition-colors duration-300 ${
                                                    isVoted ? "bg-lakou-gold text-lakou-ink" : "bg-lakou-orange hover:bg-lakou-gold text-white hover:text-lakou-ink"
                                                } disabled:opacity-40`}
                                            >
                                                <MapPin size={13} /> {isVoted ? t("vote_voted") : busy === c.id ? "…" : t("vote_btn")}
                                            </motion.button>
                                        </div>
                                    </div>
                                </Reveal>
                            );
                        })}
                        {voted === "locked" && <p className="text-xs text-lakou-sand text-center pt-2" data-testid="vote-locked-note">One vote per person per campaign.</p>}
                    </div>
                </div>
            </div>

            {notifyCity && (
                <div className="fixed inset-0 z-[80] flex items-center justify-center bg-lakou-void/80 backdrop-blur-sm px-4" data-testid="vote-notify-modal">
                    <motion.form
                        initial={{ opacity: 0, y: 24, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
                        onSubmit={subscribeCity}
                        className="w-full max-w-md rounded-3xl border border-lakou-gold/25 bg-lakou-coal p-8 space-y-5"
                    >
                        <div className="flex items-center gap-3">
                            <Bell size={18} className="text-lakou-gold" />
                            <span className="font-display font-extrabold text-lg text-lakou-cream">Get the {notifyCity.name} announcement</span>
                        </div>
                        <p className="text-sm text-lakou-grey">If {notifyCity.name} wins, you'll be first to know — dates, presale access, everything.</p>
                        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" data-testid="vote-notify-email"
                            className="w-full rounded-xl border border-lakou-gold/25 bg-lakou-raise/70 px-4 py-3 text-sm text-lakou-cream placeholder:text-lakou-sand focus:outline-none focus:border-lakou-gold" />
                        <div className="flex gap-3">
                            <button type="submit" data-testid="vote-notify-submit" className="flex-1 rounded-full bg-lakou-orange hover:bg-lakou-gold text-white hover:text-lakou-ink font-display font-bold text-xs uppercase tracking-[0.15em] py-3.5 transition-colors">Notify me</button>
                            <button type="button" onClick={() => setNotifyCity(null)} data-testid="vote-notify-skip" className="rounded-full border border-lakou-gold/25 text-lakou-grey hover:text-lakou-cream font-display font-bold text-xs uppercase tracking-[0.15em] px-6 py-3.5 transition-colors">Skip</button>
                        </div>
                    </motion.form>
                </div>
            )}
        </div>
    );
}
